"use client";

import Link from "next/link";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "@/lib/i18n";
import type { TranslationKey } from "@/lib/i18n";

type Section = { headingKey: TranslationKey; bodyKey: TranslationKey };

type Props = {
  titleKey: TranslationKey;
  introKey: TranslationKey;
  sections: Section[];
  contactKey: TranslationKey;
};

/**
 * Renders a long-form legal document (Privacy, Terms, Refund) for the marketing
 * surface. Body strings encode bullet lines with `\n• ` so we split on `\n` and
 * render bullet rows vs. plain paragraphs based on the leading character.
 */
export function LegalDocument({ titleKey, introKey, sections, contactKey }: Props) {
  const { t } = useTranslation();

  return (
    <div className="py-16 md:py-20 px-4 sm:px-6 bg-hero-pattern">
      <div className="max-w-3xl mx-auto">
        <Link
          href="/"
          className="inline-flex items-center gap-1.5 text-callout text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          {t("legal.backHome")}
        </Link>

        <header className="mb-10 anim-fade-up">
          <h1 className="text-[2rem] md:text-[2.5rem] font-semibold text-foreground tracking-tight leading-[1.1] mb-3">
            {t(titleKey)}
          </h1>
          <p className="text-footnote uppercase tracking-wider text-muted-foreground font-semibold mb-5">
            {t("legal.effective")}
          </p>
          <p className="text-[15px] text-foreground/80 leading-relaxed">
            {t(introKey)}
          </p>
        </header>

        <div className="fiori-panel p-6 sm:p-8 shadow-soft-sm">
          <div className="space-y-8">
            {sections.map(({ headingKey, bodyKey }) => (
              <section key={headingKey}>
                <h2 className="text-[18px] md:text-[20px] font-semibold text-foreground tracking-tight mb-3">
                  {t(headingKey)}
                </h2>
                <SectionBody text={t(bodyKey)} />
              </section>
            ))}
          </div>

          <div className="mt-10 pt-6 border-t border-border">
            <p className="text-callout text-muted-foreground">
              <span className="font-semibold text-foreground">{t("legal.contactPrefix")}</span>{" "}
              {t(contactKey)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionBody({ text }: { text: string }) {
  const lines = text.split("\n");

  // Group consecutive bullet lines so we render one <ul> per run.
  const blocks: Array<{ kind: "p"; text: string } | { kind: "ul"; items: string[] }> = [];
  for (const raw of lines) {
    const line = raw.trimStart();
    if (line.startsWith("• ")) {
      const item = line.slice(2);
      const last = blocks[blocks.length - 1];
      if (last && last.kind === "ul") last.items.push(item);
      else blocks.push({ kind: "ul", items: [item] });
    } else if (line.length > 0) {
      blocks.push({ kind: "p", text: line });
    }
  }

  return (
    <div className="space-y-3 text-body text-foreground/85 leading-relaxed">
      {blocks.map((block, idx) =>
        block.kind === "p" ? (
          <p key={idx}>{block.text}</p>
        ) : (
          <ul key={idx} className="list-disc ps-5 space-y-1.5 marker:text-muted-foreground">
            {block.items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        ),
      )}
    </div>
  );
}
